'use client'

import { useCallback, useEffect, useMemo, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useUser } from '@auth0/nextjs-auth0'
import { useTranslations } from 'next-intl'

import { openConfirmModal } from '../components/basic/confirmModal'
import { STORAGE_KEY } from '../constants'
import useLocalBookmarks from './useLocalBookmarks'
import useVesselJourneyBookmarks, {
  TOptionsQueryVesselJourneyBookmarks
} from './teqplay-api/useVesselJourneyBookmarks'

import { TVesselJourneyResponse } from '@/api/v1/resources/vesselJourney'
import { useAnalytics, createAnalyticsVessel } from '@/app/lib/analytics'
import { getDefaultPort } from '@/app/config/ports'
import { sortVesselsByArrivalPriority } from '@/app/utils/select/select'

export enum BookmarkAction {
  ADD = 'add',
  REMOVE = 'remove'
}

export type TUseSwitchBookmarksOptions = {
  vesselJourneyBookmarksOptions?: Partial<TOptionsQueryVesselJourneyBookmarks> & {
    enabled?: boolean
  }
}

const useSwitchBookmarks = (options?: TUseSwitchBookmarksOptions) => {
  const { vesselJourneyBookmarksOptions = { enabled: true } } = options || {}
  const t = useTranslations()
  const { user } = useUser()
  const queryClient = useQueryClient()
  const { trackFleetAdd, trackFleetRemove } = useAnalytics()
  const [loadingVesselImo, setLoadingVesselImo] = useState<string | null>(null)

  const {
    bookmarks: localBookmarks,
    addBookmarkNow: addLocalBookmarkNow,
    removeBookmarkNow: removeLocalBookmarkNow
  } = useLocalBookmarks()
  const {
    loading,
    bookmarks: remoteBookmarks,
    post: { mutate: addRemoteBookmark, isPending: isAddingBookmark },
    delete: { mutate: removeRemoteBookmark, isPending: isRemovingBookmark },
    isFetching: isFetchingRemoteBookmarks
  } = useVesselJourneyBookmarks(vesselJourneyBookmarksOptions)

  const isBookmarkLoading = useMemo(() => {
    return isAddingBookmark || isRemovingBookmark || isFetchingRemoteBookmarks
  }, [isAddingBookmark, isRemovingBookmark, isFetchingRemoteBookmarks])

  // When user logs in, migrate local bookmarks to remote. remove local bookmarks after migration
  useEffect(() => {
    if (user && localBookmarks && localBookmarks.length > 0 && !loading) {
      // Waiting for the user session to be fully established before migrating bookmarks
      const timeoutId = setTimeout(() => {
        localBookmarks.forEach((v) => {
          addRemoteBookmark(
            { imos: [v.ship.imo], returnFinalSubscriptionList: false },
            {
              onSuccess: () => removeLocalBookmarkNow(v)
            }
          )
        })
      }, 1000)

      // Cleanup timeout on unmount or dependency change
      return () => clearTimeout(timeoutId)
    }
    return undefined
  }, [user, localBookmarks, addRemoteBookmark, removeLocalBookmarkNow, loading])

  const bookmarks = useMemo(() => {
    if (loading) {
      return []
    } else if (user) {
      return remoteBookmarks
    } else {
      return localBookmarks
    }
  }, [loading, user, remoteBookmarks, localBookmarks])

  // Sorting function for bookmarks (recent dates first)
  const sortedBookmarks = useMemo(() => {
    if (!bookmarks) return undefined
    return sortVesselsByArrivalPriority(bookmarks, {
      priorityPortUnlocode: getDefaultPort().unlocode
    })
  }, [bookmarks])

  const updateBookmarkCache = (vessel: TVesselJourneyResponse, action: BookmarkAction) => {
    queryClient.setQueryData(
      ['v1', 'vesselJourney', 'bookmarks'],
      (old: TVesselJourneyResponse[] | undefined) => {
        if (!old) return action === BookmarkAction.ADD ? [vessel] : []
        const exists = old.some((v) => v.ship.imo === vessel.ship.imo)
        if (action === BookmarkAction.ADD) return exists ? old : [...old, vessel]
        return old.filter((v) => v.ship.imo !== vessel.ship.imo)
      }
    )
  }

  const isVesselBookmarkLoading = useCallback(
    (imo: string) => {
      return loadingVesselImo === imo
    },
    [loadingVesselImo]
  )

  const modifyBookmarkNow = (vessel: TVesselJourneyResponse, action: BookmarkAction) => {
    if (!vessel?.ship?.imo) {
      throw Error('Invalid vessel data: missing IMO')
    }
    if (user) {
      // Set loading state for this specific vessel
      setLoadingVesselImo(vessel.ship.imo)

      updateBookmarkCache(vessel, action)

      const apiCall = action === BookmarkAction.ADD ? addRemoteBookmark : removeRemoteBookmark

      apiCall(
        {
          imos: [vessel.ship.imo],
          returnFinalSubscriptionList: false
        },
        {
          onSuccess: () => {
            // Track analytics event on successful API call
            const currentFleetSize = bookmarks?.length || 0
            const vesselForAnalytics = createAnalyticsVessel(vessel)
            if (action === BookmarkAction.ADD) {
              trackFleetAdd(vesselForAnalytics, currentFleetSize + 1)
            } else {
              trackFleetRemove(vesselForAnalytics, Math.max(0, currentFleetSize - 1))
            }
          },
          onError: () => {
            // Revert optimistic update on error
            const revertAction =
              action === BookmarkAction.ADD ? BookmarkAction.REMOVE : BookmarkAction.ADD
            updateBookmarkCache(vessel, revertAction)
          },
          onSettled: () => {
            // Clear loading state when API call completes (success or error)
            setLoadingVesselImo(null)
          }
        }
      )
    } else {
      // Get the current fleet size before the operation
      const currentFleetSize = localBookmarks?.length || 0
      const localCall = action === BookmarkAction.ADD ? addLocalBookmarkNow : removeLocalBookmarkNow
      localCall(vessel)

      // Trigger a fake query invalidation to notify other components
      queryClient.invalidateQueries({ queryKey: ['localBookmarks'] })

      // Track analytics for local bookmarks with the expected new fleet size
      const newFleetSize =
        action === BookmarkAction.ADD ? currentFleetSize + 1 : Math.max(0, currentFleetSize - 1)
      const vesselForAnalytics = createAnalyticsVessel(vessel)
      if (action === BookmarkAction.ADD) {
        trackFleetAdd(vesselForAnalytics, newFleetSize)
      } else {
        trackFleetRemove(vesselForAnalytics, newFleetSize)
      }
    }
  }

  const addBookmarkNow = (v: TVesselJourneyResponse) => modifyBookmarkNow(v, BookmarkAction.ADD)
  const removeBookmarkNow = (v: TVesselJourneyResponse) =>
    modifyBookmarkNow(v, BookmarkAction.REMOVE)

  const getIsVesselBookmarked = useCallback(
    (imo: string) => {
      if (!imo || !bookmarks) return false
      return bookmarks.some((v) => v?.ship?.imo === imo)
    },
    [bookmarks]
  )

  function canUseSessionStorage() {
    try {
      return typeof window !== 'undefined' && !!window.sessionStorage
    } catch {
      return false
    }
  }

  function getSkipRemoveConfirm(): boolean {
    if (!canUseSessionStorage()) return false
    try {
      return window.sessionStorage.getItem(STORAGE_KEY.SKIP_REMOVE_CONFIRM) === '1'
    } catch {
      return false
    }
  }

  function setSkipRemoveConfirm(val: boolean) {
    if (!canUseSessionStorage()) return
    try {
      if (val) window.sessionStorage.setItem(STORAGE_KEY.SKIP_REMOVE_CONFIRM, '1')
      else window.sessionStorage.removeItem(STORAGE_KEY.SKIP_REMOVE_CONFIRM)
    } catch (e) {
      console.error('Failed to set skip remove confirm', e)
    }
  }

  const handleRemoveBookmark = (vessel: TVesselJourneyResponse) => {
    if (getSkipRemoveConfirm()) {
      removeBookmarkNow(vessel)
      return
    }

    const ConfirmDescription = () => {
      const [checked, setChecked] = useState<boolean>(getSkipRemoveConfirm())
      return (
        <span>
          <span>{t('Modal.confirmUnfollowDescription')}</span>
          <label className="flex gap-2xs py-xs subtext">
            <input
              type="checkbox"
              checked={checked}
              onChange={(e) => {
                setChecked(e.currentTarget.checked)
                setSkipRemoveConfirm(e.currentTarget.checked)
              }}
            />
            <span>{t('Modal.dontAskAgainUnfollow')}</span>
          </label>
        </span>
      )
    }

    openConfirmModal({
      title: t('Modal.confirmUnfollowTitle'),
      description: <ConfirmDescription />,
      confirmLabel: t('Modal.confirmUnfollowConfirmLabel'),
      cancelLabel: t('Modal.confirmFollowMigrationCancelLabel'),
      onConfirm: () => removeBookmarkNow(vessel)
    })
  }

  return {
    loading,
    bookmarks,
    sortedBookmarks,
    localBookmarks,
    addBookmarkNow,
    handleRemoveBookmark,
    getIsVesselBookmarked,
    isBookmarkLoading,
    isVesselBookmarkLoading
  }
}

export default useSwitchBookmarks
